/**
 * StockItemPickerModal — aranabilir stok kalemi seçici modal.
 *
 * Manuel düzeltme / fire gibi akışlarda StockItem (kalem × lokasyon) bu
 * modaldan seçilir. Arama SERVER-SIDE'dır
 * (debounced → /stock-items/?search=...): yüzlerce kalem olsa bile DOM'a
 * yalnız eşleşen sayfa kadar satır basılır — düz Select gibi tüm listeyi
 * yüklemez.
 *
 * Görüntüleme: kalem adı + birim + lokasyon + mevcut miktar.
 * StockItem hem hammadde hem ürün taşıyabilir (ingredient_name VEYA
 * product_name); biri doluysa onu göster.
 */
import { useState } from "react";
import {
  Badge, Box, Button, Group, Loader, Modal, ScrollArea, Stack, Text, TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteResource } from "../../../shared/api";
import { formatQty, useInfiniteScrollRef } from "../../../shared/ui";

export interface StockItemPickerRow {
  id: string;
  ingredient?: string | null;        // FK id (transfer/waste için)
  product?: string | null;            // FK id
  location?: string | null;           // FK id
  ingredient_name?: string | null;
  product_name?: string | null;
  location_name?: string | null;
  kind?: "INGREDIENT" | "PRODUCT";
  quantity?: string;
  unit?: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Seçilen kalem — modal kapanır. */
  onSelect: (item: StockItemPickerRow) => void;
  /** Listeden gizlenecek kalem id'leri (zaten seçili olanlar). */
  excludeIds?: string[];
  /** Yalnız hammadde/ürün filtresi. Boş bırakılırsa ikisi de gösterilir. */
  kind?: "INGREDIENT" | "PRODUCT" | "";
  title?: string;
}

function displayName(r: StockItemPickerRow): string {
  return r.product_name || r.ingredient_name || "(adsız)";
}

export function StockItemPickerModal({
  opened, onClose, onSelect, excludeIds = [], kind = "", title = "Stok Kalemi Seç",
}: Props) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);

  const filters: Record<string, any> = {};
  if (kind) filters.kind = kind;

  const {
    items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<StockItemPickerRow>("stock-items", {
    search: debounced,
    filters,
  });

  const excluded = new Set(excludeIds);
  const visible = items.filter((i) => !excluded.has(i.id));

  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    itemCount: items.length,
  });

  return (
    // Drawer (z-index 200) içinden açıldığında üstünde durması için
    // zIndex'i yükseltiyoruz; bekleyen pencereye odaklanmaması sorunu çözer.
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      zIndex={300}
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Hammadde/ürün adı veya SKU ile arayın ve listeden seçin
        </Text>
        <TextInput
          placeholder="Adı veya SKU ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />

        <ScrollArea.Autosize mah={420} offsetScrollbars>
          {isLoading ? (
            <Group justify="center" py="xl"><Loader /></Group>
          ) : visible.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              {debounced ? "Eşleşen kayıt yok" : "Henüz stok kalemi yok"}
            </Text>
          ) : (
            <Stack gap={6}>
              {visible.map((i) => (
                <UnstyledButton
                  key={i.id}
                  onClick={() => { onSelect(i); onClose(); }}
                  style={{
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: 10,
                  }}
                >
                  <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Box style={{ minWidth: 0 }}>
                      <Group gap={6} wrap="nowrap">
                        <Text fw={600} size="sm" truncate>
                          {displayName(i)}
                        </Text>
                        {i.kind === "PRODUCT" && (
                          <Badge size="xs" color="violet" variant="light">Ürün</Badge>
                        )}
                        {i.kind === "INGREDIENT" && (
                          <Badge size="xs" color="blue" variant="light">Hammadde</Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" truncate>
                        {i.location_name || "—"}
                      </Text>
                    </Box>
                    <Box style={{ flexShrink: 0, textAlign: "right" }}>
                      <Text size="sm" fw={500}>
                        {formatQty(i.quantity)} {i.unit || ""}
                      </Text>
                      <Text size="xs" c="dimmed">mevcut</Text>
                    </Box>
                  </Group>
                </UnstyledButton>
              ))}
              {hasNextPage && (
                <Box ref={sentinelRef}>
                  <Group justify="center" py="sm"><Loader size="xs" /></Group>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea.Autosize>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
